import { z } from 'zod'

import { getImageValidationError, isImageFileLike, type ImageFileLike } from './image-validation'
import {
  normalizeBusinessActivity,
  normalizeCompanyName,
  normalizeWebsiteUrl,
  parseEmailChips,
  tryNormalizeWebsiteUrl,
  type EmailChipsValue,
} from './normalization'

export const emailChipsSchema = z
  .object({
    items: z.array(z.string()),
    draft: z.string(),
  })
  .transform((value, context) => {
    const parsed = parseEmailChips(value)

    if (parsed.invalidEmails.length > 0) {
      context.addIssue({
        code: 'custom',
        path: ['draft'],
        message: `Corrige o elimina ${
          parsed.invalidEmails.length === 1 ? 'este correo inválido' : 'estos correos inválidos'
        }: ${parsed.invalidEmails.join(', ')}.`,
      })
    }

    if (parsed.emails.length === 0) {
      context.addIssue({
        code: 'custom',
        message: 'Agrega al menos un correo electrónico válido.',
      })
    }

    return { items: parsed.emails, draft: '' } satisfies EmailChipsValue
  })

export const websiteUrlSchema = z
  .string()
  .trim()
  .max(2_048, 'La URL es demasiado larga.')
  .transform((value, context) => {
    if (!value) {
      context.addIssue({ code: 'custom', message: 'Ingresa la URL del sitio web.' })
      return z.NEVER
    }

    const normalized = tryNormalizeWebsiteUrl(value)
    if (!normalized) {
      context.addIssue({
        code: 'custom',
        message: 'Ingresa una URL web válida (http o https).',
      })
      return z.NEVER
    }

    return normalized.websiteUrl
  })

export const optionalImageSchema = z
  .custom<ImageFileLike | null>(
    (value) => value === null || isImageFileLike(value),
    'Selecciona un archivo de imagen válido.',
  )
  .superRefine((value, context) => {
    if (value === null || !isImageFileLike(value)) return

    const error = getImageValidationError(value)
    if (error) context.addIssue({ code: 'custom', message: error })
  })

export const companyFormSchema = z.object({
  name: z
    .string()
    .transform(normalizeCompanyName)
    .pipe(
      z
        .string()
        .min(1, 'Ingresa el nombre de la empresa.')
        .max(160, 'El nombre no puede superar 160 caracteres.'),
    ),
  businessActivity: z
    .string()
    .transform(normalizeBusinessActivity)
    .pipe(
      z
        .string()
        .min(1, 'Ingresa la actividad o tipo de empresa.')
        .max(200, 'La actividad no puede superar 200 caracteres.'),
    ),
  websiteUrl: websiteUrlSchema,
  emails: emailChipsSchema,
  image: optionalImageSchema,
})

export const editCompanyFormSchema = companyFormSchema.extend({
  removeCurrentImage: z.boolean(),
})

export type CompanyFormValues = z.input<typeof companyFormSchema>
export type CompanyFormData = z.output<typeof companyFormSchema>
export type EditCompanyFormValues = z.input<typeof editCompanyFormSchema>
export type EditCompanyFormData = z.output<typeof editCompanyFormSchema>

export interface NormalizedCompanySubmission extends Omit<CompanyFormData, 'websiteUrl'> {
  websiteUrl: string
  websiteNormalized: string
}

/** Produce exactamente los dos valores de URL requeridos por la capa de datos. */
export function toNormalizedCompanySubmission(
  values: CompanyFormValues,
): NormalizedCompanySubmission {
  const parsed = companyFormSchema.parse(values)
  const website = normalizeWebsiteUrl(parsed.websiteUrl)
  return {
    ...parsed,
    websiteUrl: website.websiteUrl,
    websiteNormalized: website.websiteNormalized,
  }
}
